import { formatForeignAddress, formatLocalAddress } from '@/shared/composables/useAddressFormatter';

export type SgAddressType = 'B07001' | 'B07002';

/** Entity-style SG address (local or foreign) */
export interface SgAddress {
  addressTypeCd: SgAddressType;
  postalCode: string;
  blockNo: string;
  streetName: string;
  buildingName: string;
  level: string;
  unit: string;
  foreignAddressLine1: string;
  foreignAddressLine2: string;
  /** false = 층/호수 없음 (bizfile "Address doesn't have level and unit") */
  haveLevelUnit?: boolean;
}

/** @deprecated use SgAddress */
export type SgLocalAddress = SgAddress;

export function createEmptySgAddress(): SgAddress {
  return {
    addressTypeCd: 'B07001',
    postalCode: '',
    blockNo: '',
    streetName: '',
    buildingName: '',
    level: '',
    unit: '',
    foreignAddressLine1: '',
    foreignAddressLine2: '',
    haveLevelUnit: true,
  };
}

export const createEmptySgLocalAddress = createEmptySgAddress;

export function formatSgAddressLine(address: SgAddress | string | null | undefined): string {
  if (!address) return '';
  if (typeof address === 'string') return address.trim();

  if (address.addressTypeCd === 'B07002') {
    const formatted = formatForeignAddress(
      {
        foreignAddressLine1: address.foreignAddressLine1,
        foreignAddressLine2: address.foreignAddressLine2,
      },
      { jurisdiction: 'SG' },
    );
    return typeof formatted === 'string' ? formatted : formatted.join(', ');
  }

  const formatted = formatLocalAddress(
    {
      postalCode: address.postalCode,
      blockNo: address.blockNo,
      streetName: address.streetName,
      buildingName: address.buildingName,
      level: address.level,
      unit: address.unit,
    },
    { jurisdiction: 'SG' },
  );
  return typeof formatted === 'string' ? formatted : formatted.join(', ');
}

export const formatSgLocalAddressLine = formatSgAddressLine;

export function normalizeSgAddress(value: SgAddress | string | null | undefined): SgAddress {
  if (!value || typeof value === 'string') {
    return createEmptySgAddress();
  }
  const merged: SgAddress = {
    ...createEmptySgAddress(),
    ...value,
    addressTypeCd: value.addressTypeCd === 'B07002' ? 'B07002' : 'B07001',
  };
  // 데이터 보정: 타입은 local(B07001) 인데 foreign 라인에만 내용이 있고 local 필드가 모두 비었으면
  // 실제로는 foreign 주소인데 타입이 잘못 저장된 것 → foreign(B07002) 으로 교정.
  // (이 값이 SgAddressFields 표시와 toBizAddress payload 양쪽을 결정하므로 교정하지 않으면 주소가 유실됨)
  if (merged.addressTypeCd !== 'B07002') {
    const hasForeign =
      !!String(merged.foreignAddressLine1 || '').trim() ||
      !!String(merged.foreignAddressLine2 || '').trim();
    const hasLocal =
      !!String(merged.postalCode || '').trim() ||
      !!String(merged.blockNo || '').trim() ||
      !!String(merged.streetName || '').trim() ||
      !!String(merged.buildingName || '').trim() ||
      !!String(merged.level || '').trim() ||
      !!String(merged.unit || '').trim();
    if (hasForeign && !hasLocal) {
      merged.addressTypeCd = 'B07002';
    }
  }
  return merged;
}

export function normalizeSgAddressFromLegacy(value: SgAddress | string | null | undefined): SgAddress {
  if (typeof value === 'string' && value.trim()) {
    return {
      ...createEmptySgAddress(),
      addressTypeCd: 'B07002',
      foreignAddressLine1: value.trim(),
    };
  }
  return normalizeSgAddress(value);
}

export const normalizeSgLocalAddress = normalizeSgAddress;
